<template>
  <!-- 登録済み一覧のカード（雇用指示1行 or 契約詳細1件）。閲覧のみ -->
  <div class="card" :class="row.isEnded ? 'ended' : ''">
    <!-- 1行目：物件名（2段の物件名も1行にする）と終了バッジ -->
    <div class="card-head">
      <div class="site-name">
        {{ siteNameText }}
        <!-- 終了バッジは物件名のすぐ横に付ける -->
        <Badge v-if="row.isEnded" color="red" class="ml-2 align-middle">終了</Badge>
      </div>
      <div class="ml-auto text-xs text-gray-500">
        <template v-if="isEmployment">
          物件コード：{{ row.cdSite }}<span class="ml-3">シーケンス：{{ row.sequenceNumber }}</span><span v-if="row.changeFromSequenceNumber">（契約変更前：{{ row.changeFromSequenceNumber }}）</span>
        </template>
        <template v-else>
          物件コード：{{ row.cdSite }}<span class="ml-3">営業所：{{ row.cdSalesOffice || '-' }}</span>
        </template>
      </div>
    </div>

    <!-- 物件名のすぐ下：物件住所・得意先名 -->
    <div v-if="row.siteAddress || row.client1 || row.client2" class="text-xs text-gray-600 mb-3">
      <span v-if="row.siteAddress">物件住所：{{ row.siteAddress }}</span>
      <span v-if="row.client1 || row.client2" :class="row.siteAddress ? 'ml-6' : ''">得意先：{{ row.client1 }} {{ row.client2 }}</span>
    </div>

    <!-- 契約コード・作業内容・開始日・終了日（見出し付きの札で、何の値かが一目で分かるようにする） -->
    <div class="chips">
      <div class="chip">
        <span class="chip-label">契約コード</span>
        <span class="chip-value">{{ row.cdContract || '-' }}</span>
      </div>
      <div class="chip">
        <span class="chip-label">作業内容</span>
        <span class="chip-value">{{ row.contentsWorkName || row.cdContentsWork || '-' }}</span>
      </div>
      <div class="chip">
        <span class="chip-label">{{ isEmployment ? '開始日' : '契約開始' }}</span>
        <span class="chip-value">{{ slash(startDate) || '-' }}</span>
      </div>
      <div class="chip" :class="row.isEnded ? 'chip-ended' : ''">
        <span class="chip-label">{{ isEmployment ? '終了日' : '契約終了' }}</span>
        <span class="chip-value">{{ slash(endDate) || '-' }}</span>
      </div>
    </div>

    <!-- 曜日ごとの勤務（角丸の表。曜日の見出しは藍色、土曜は青・日曜は赤。勤務がある日は薄い藍色、休みは薄い灰色） -->
    <div class="week-wrap">
      <table class="week-table">
        <thead>
          <tr>
            <th></th>
            <th v-for="w in weekList" :key="w.id" :class="[isOff(w) ? 'rest' : '', w.key === 'sat' ? 'sat' : '', w.key === 'sun' ? 'sun' : '']">{{ w.name }}</th>
          </tr>
        </thead>
        <!-- 上から勤務形態・時間・実働。勤務ありのクラスは work（共通CSS の .on が緑なので使わない） -->
        <tbody>
          <tr>
            <th>勤務形態</th>
            <td v-for="w in weekList" :key="w.id" :class="isOff(w) ? 'rest' : 'work'">
              <template v-if="isOff(w)">-</template>
              <template v-else>
                <span v-for="(name, ai) in attendanceList(row['attendance' + w.id])" :key="ai" class="att-pill">{{ name }}</span>
              </template>
            </td>
          </tr>
          <tr>
            <th>時間</th>
            <td v-for="w in weekList" :key="w.id" class="time" :class="isOff(w) ? 'rest' : 'work'">
              <template v-if="isOff(w)">-</template>
              <template v-else>{{ row['start' + w.id] || '-' }}<span class="tilde">～</span>{{ row['end' + w.id] || '-' }}</template>
            </td>
          </tr>
          <tr>
            <th>実働</th>
            <td v-for="w in weekList" :key="w.id" :class="isOff(w) ? 'rest' : 'work'">{{ isOff(w) ? '-' : hoursText(row['workingHours' + w.id]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 祝日・ゴミ出し・全社休日出勤・時間指定（上の札と同じ形。有は藍色にして一目で分かるようにする） -->
    <div class="chips flags">
      <div class="chip">
        <span class="chip-label">祝日</span>
        <span class="chip-value" :class="holidayClass(holidayCode)">{{ holidayName(holidayCode) }}</span>
      </div>
      <div v-for="f in flags" :key="f.label" class="chip">
        <span class="chip-label">{{ f.label }}</span>
        <span class="chip-value" :class="flagClass(f.value)">{{ yesNo(f.value) }}</span>
      </div>
      <div class="text-xs text-gray-500 ml-auto">更新日時：{{ row.updateDate }}</div>
    </div>

    <!-- 賃金（雇用指示の行だけ。曜日表と同じ角丸・藍色の見出し。今日の時点で効いている行は薄い藍色にして「適用中」を付ける） -->
    <div v-if="isEmployment" class="mt-3">
      <div class="text-sm font-bold text-gray-700 mb-1">賃金</div>
      <div v-if="!wages.length" class="empty-text">賃金の登録はありません。</div>
      <div v-else class="wage-wrap">
        <table class="wage-table">
          <thead>
            <tr>
              <th class="period">期間</th>
              <th></th>
              <th class="wage">基本時給</th>
              <th class="fare">電車</th>
              <th class="fare">バス</th>
              <th class="fare">駐輪</th>
              <th>更新日時</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="w in wages" :key="w.id" :class="wageState(w)">
              <!-- 期間（終了日が無ければ「～」だけ） -->
              <td class="period">{{ slash(w.startDate) }}<span class="tilde">～</span>{{ slash(w.endDate) }}</td>
              <td class="state">
                <Badge v-if="wageState(w) === 'ended'" color="red">終了</Badge>
                <Badge v-else-if="wageState(w) === 'future'" color="blue">予定</Badge>
                <Badge v-else color="indigo">適用中</Badge>
              </td>
              <td class="wage">{{ yen(w.basicWage) }}</td>
              <td v-for="f in fareList(w)" :key="f.label" class="fare">
                <template v-if="f.amount === null">{{ f.name }}</template>
                <template v-else><span class="fare-kubun">{{ f.name }}</span>{{ f.amount }}</template>
              </td>
              <td class="text-xs text-gray-500">{{ w.updateDate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 備考（雇用指示の行だけ。賃金の表の下） -->
    <div v-if="isEmployment && row.note" class="note">備考：{{ row.note }}</div>
  </div>
</template>

<script>
import Badge from '@components/Badge.vue'
import { WEEK_LIST, PROVISION_SELECTIONS } from '@libs/constantsAKBS'
import * as utilsAKBS from '@libs/utilsAKBS'

export default {
  components: {
    Badge
  },

  props: {
    // 雇用指示の行（wages 付き）か契約詳細の行
    row: {
      type: Object,
      required: true
    },
    // 'employment'（雇用指示）か 'contract'（契約詳細）
    kind: {
      type: String,
      default: 'employment'
    },
    // 終了した賃金も出すか
    showEnded: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      weekList: WEEK_LIST,
      // 今日（yyyy-mm-dd。賃金の開始日と文字列で比べる）
      today: new Date().toLocaleDateString('sv-SE')
    }
  },

  computed: {
    isEmployment() {
      return this.kind === 'employment'
    },

    /**
     * 物件名（バックは2段の物件名を改行でつないでいるので、空白にして1行で出す）
     */
    siteNameText() {
      return String(this.row.siteName || this.row.cdSite || '').replace(/\n/g, ' ')
    },

    startDate() {
      return this.isEmployment ? this.row.startDateWork : this.row.startDateContract
    },

    endDate() {
      return this.isEmployment ? this.row.endDateWork : this.row.endDateContract
    },

    /**
     * 祝日区分のコード（雇用指示と契約詳細でキーが違う）
     */
    holidayCode() {
      return this.isEmployment ? this.row.cdKubunHoliday : this.row.kubunHolidayContract
    },

    /**
     * 有・無で出す項目（ゴミ出し・全社休日出勤。契約詳細は時間指定も）
     */
    flags() {
      const row = this.row
      if (this.isEmployment) {
        return [
          { label: 'ゴミ出し', value: row.hasCarryingoutGarbage },
          { label: '全社休日出勤', value: row.hasExportingCompanysHoliday }
        ]
      }
      return [
        { label: 'ゴミ出し', value: row.hasCarryingoutGarbageContract },
        { label: '全社休日出勤', value: row.hasExportingCompanysHolidayContract },
        { label: '時間指定', value: row.specificTime }
      ]
    },

    /**
     * 賃金（終了した分は showEnded のときだけ）
     */
    wages() {
      const wages = this.row.wages || []
      return this.showEnded ? wages : wages.filter(w => !w.isEnded)
    }
  },

  methods: {
    // 表示用の文字は utilsAKBS と共用（雇用指示を軸にした一覧表と同じ見せ方にする）
    isOff(w) {
      return utilsAKBS.isDayOff(this.row, w)
    },

    attendanceList(codes) {
      return utilsAKBS.attendanceNames(codes)
    },

    hoursText(hours) {
      return utilsAKBS.hoursText(hours)
    },

    holidayName(code) {
      return utilsAKBS.holidayName(code)
    },

    yesNo(val) {
      return utilsAKBS.yesNo(val)
    },

    /**
     * 祝日区分の色（無（0）と未設定以外は藍色にする。FULL・1時間…は出勤があるので有と同じ扱い）
     */
    holidayClass(code) {
      if (code === null || code === undefined || code === '') {
        return ''
      }
      return Number(code) === 0 ? '' : 'flag-yes'
    },

    /**
     * 有・無の色（有だけ藍色にする。無は通常の色のまま）
     */
    flagClass(val) {
      return Number(val) === 1 ? 'flag-yes' : ''
    },

    /**
     * 賃金の行の状況（ended：終了、future：開始日がまだ来ていない、current：今日の時点で効いている）
     * @param {Object} w 賃金
     */
    wageState(w) {
      if (w.isEnded) {
        return 'ended'
      }
      return w.startDate && String(w.startDate) > this.today ? 'future' : 'current'
    },

    /**
     * 交通費3つ（電車・バス・駐輪）を表の列にする
     * @param {Object} w 賃金
     */
    fareList(w) {
      return [
        { label: 'train', ...this.fareParts(w.kubunTrain, w.fareTrain) },
        { label: 'bus', ...this.fareParts(w.kubunBus, w.fareBus) },
        { label: 'bicycle', ...this.fareParts(w.kubunBicycleParking, w.fareBicycleParking) }
      ]
    },

    /**
     * 交通費の表示（区分の名前と金額。無は「0」、未設定は「-」で金額なし）
     * 1日 500円 / 1ヶ月 10,000円 / 0 / -
     */
    fareParts(kubun, fare) {
      if (kubun === null || kubun === undefined || kubun === '') {
        return { name: '-', amount: null }
      }
      if (Number(kubun) === 0) {
        return { name: '0', amount: null }
      }
      const p = PROVISION_SELECTIONS.find(x => Number(x.id) === Number(kubun))
      return { name: p ? p.name : String(kubun), amount: this.yen(fare) }
    },

    /**
     * 金額の表示（1,200円。無ければ -）
     */
    yen(val) {
      if (val === null || val === undefined || val === '') {
        return '-'
      }
      return `${Number(val).toLocaleString()}円`
    },

    /**
     * yyyy-mm-dd → yyyy/mm/dd（無ければ空）
     */
    slash(day) {
      return day ? String(day).replace(/-/g, '/') : ''
    }
  }
}
</script>

<style scoped>
/* 1件ごとの枠（中の札や表より濃く太くして、件の区切りが分かるようにする） */
.card {
  border: 2px solid #6366f1;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #fff;
}

.card.ended {
  border-color: #9ca3af;
  background: #fafafa;
}

.card-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 16px;
  margin-bottom: 4px;
}

.site-name {
  font-size: 1.05rem;
  font-weight: bold;
  color: #1d4ed8;
  white-space: nowrap;
}

/* 契約コード・作業内容・開始日・終了日の札（全部同じ幅にそろえる。長い値の札だけ伸びる） */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin: 4px 0 8px;
}

.chip {
  display: inline-flex;
  align-items: stretch;
  min-width: 190px;
  border: 1px solid #c7d2fe;
  border-radius: 4px;
  overflow: hidden;
  font-size: 0.8rem;
  line-height: 1.2;
}

/* 見出しは一番長い「全社休日出勤」に合わせた固定幅で中央そろえ */
.chip-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  flex-shrink: 0;
  padding: 3px 6px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.7rem;
  white-space: nowrap;
}

/* 値は残りの幅いっぱいに広げて中央そろえ */
.chip-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 3px 10px;
  background: #fff;
  color: #1f2937;
  font-weight: bold;
  white-space: nowrap;
}

/* 曜日表の下の札（祝日・ゴミ出し…）。更新日時を右端にそろえる */
.chips.flags {
  align-items: center;
  margin: 8px 0 0;
}

/* 有・無の値（有は藍色。無も大事な情報なので薄くしない） */
.chip-value.flag-yes {
  color: #3730a3;
}

/* 終了している行は終了日の札を目立たせる */
.chip-ended {
  border-color: #fca5a5;
}

.chip-ended .chip-label {
  background: #fee2e2;
  color: #b91c1c;
}

.chip-ended .chip-value {
  color: #b91c1c;
}

/* 曜日ごとの勤務（角丸の枠。罫線は薄く、見出しは藍色系） */
.week-wrap {
  display: inline-block;
  max-width: 100%;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  overflow: hidden;
}

.week-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  table-layout: fixed;
}

.week-table th,
.week-table td {
  padding: 4px 6px;
  text-align: center;
  width: 112px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.week-table th:last-child,
.week-table td:last-child {
  border-right: none;
}

.week-table tbody tr:last-child th,
.week-table tbody tr:last-child td {
  border-bottom: none;
}

/* 曜日の見出し（藍色。土曜は青、日曜は赤。休みの曜日は薄く） */
.week-table thead th {
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  border-bottom-color: #c7d2fe;
}

.week-table thead th.sat {
  color: #1d4ed8;
}

.week-table thead th.sun {
  color: #dc2626;
}

.week-table thead th.rest {
  background: #f3f4f6;
  color: #c4c8cf;
}

/* 行の見出し（時間・実働・勤務形態。幅は他の列と同じ） */
.week-table tbody th {
  background: #f8f9ff;
  color: #4b5563;
  font-weight: normal;
  font-size: 0.75rem;
  text-align: left;
  padding-left: 10px;
}

/* 勤務がある日は白に近い藍色、休みはごく薄い灰色に「-」 */
.week-table td.work {
  background: #fbfcff;
  color: #1f2937;
}

.week-table td.rest {
  background: #fcfcfc;
  color: #e5e7eb;
}

/* 時間は太字で目立たせる */
.week-table td.time {
  font-weight: bold;
  white-space: nowrap;
}

.week-table td.time.rest {
  font-weight: normal;
}

.week-table .tilde {
  margin: 0 3px;
  color: #9ca3af;
  font-weight: normal;
}

/* 勤務形態は小さな丸い札（複数あれば並ぶ） */
.att-pill {
  display: inline-block;
  margin: 1px 1px;
  padding: 0 7px;
  border-radius: 9999px;
  border: 1px solid #a5b4fc;
  background: #fff;
  color: #3730a3;
  font-size: 0.68rem;
  line-height: 1.5;
}

/* 賃金の表（曜日表と同じ角丸の枠。罫線は薄く、見出しは藍色系） */
.wage-wrap {
  display: inline-block;
  max-width: 100%;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  overflow: hidden;
}

.wage-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  white-space: nowrap;
}

.wage-table th,
.wage-table td {
  padding: 5px 12px;
  text-align: center;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.wage-table th:last-child,
.wage-table td:last-child {
  border-right: none;
}

.wage-table tbody tr:last-child td {
  border-bottom: none;
}

.wage-table thead th {
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  font-size: 0.75rem;
  border-bottom-color: #c7d2fe;
}

.wage-table tbody td {
  background: #fff;
  color: #1f2937;
}

/* 期間は左そろえ、「～」は薄く */
.wage-table .period {
  text-align: left;
}

.wage-table .tilde {
  margin: 0 4px;
  color: #9ca3af;
}

/* 基本時給・電車・バス・駐輪は同じ幅にそろえる */
.wage-table .wage,
.wage-table .fare {
  width: 128px;
  box-sizing: border-box;
}

/* 基本時給は大きめの太字で目立たせる（他の列と同じく中央そろえ） */
.wage-table td.wage {
  font-size: 0.95rem;
  font-weight: bold;
  color: #1e3a8a;
}

/* 交通費は区分（1日・1ヶ月）を小さく添えて金額を出す */
.wage-table .fare-kubun {
  display: inline-block;
  margin-right: 5px;
  padding: 0 5px;
  border-radius: 3px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.68rem;
  line-height: 1.6;
}

/* 今日の時点で効いている行は薄い藍色 */
.wage-table tr.current td {
  background: #f5f7ff;
}

/* 終了した行は文字を薄く */
.wage-table tr.ended td {
  color: #9ca3af;
}

.wage-table tr.ended td.wage {
  color: #9ca3af;
}

.note {
  margin-top: 6px;
  padding: 4px 8px;
  font-size: 0.8rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  white-space: pre-wrap;
}

.empty-text {
  font-size: 0.875rem;
  color: #9ca3af;
}
</style>
